import { servicePost } from "./client.ts";

/**
 * The announcements are the existing global announcements (`Web_Admin_Global_Announcements`), which the classic start
 * page shows too — so a title, a message, a period and the partner and module they are for. Times are UTC, zoneless
 * ("2026-11-14T08:00:00"): read them with `parseUtcTimestamp`.
 */
export interface Announcement {
  AnnouncementId: number;
  Title: string;
  Body: string;
  StartsAt: string;
  EndsAt: string | null;
  /** The partner it is for; -1 = every partner. Filled in by the manager list. */
  PartnerId: number | null;
  /** The module it is for; -1 = every module. Filled in by the manager list. */
  ModuleId: number | null;
  /** True when the announcement is limited to one partner or module. Manager list only. */
  LimitedAudience: boolean;
  /** Users at the signed-in endpoint who have read it. Manager list only. */
  ReadCount: number;
  /** The attached PDF's file name, or null. The file is fetched on demand (`getAnnouncementAttachment`). */
  AttachmentName: string | null;
  /** Bytes; 0 when there is no attachment. */
  AttachmentSize: number;
}

export interface AnnouncementAttachment {
  FileName: string;
  ContentType: string;
  SizeBytes: number;
  ContentBase64: string;
}

/** A read receipt carries no time: the table only knows which user read which announcement. */
export interface AnnouncementReader {
  UserId: number;
  UserName: string | null;
  FullName: string | null;
  Email: string | null;
}

export interface AnnouncementReaders {
  /** The readers at the endpoint the SuperAdmin is signed in to. */
  Readers: AnnouncementReader[];
  ReadCount: number;
}

/** What a SuperAdmin sends when creating (AnnouncementId 0) or editing an announcement. */
export interface AnnouncementInput {
  AnnouncementId: number;
  Title: string;
  Body: string;
  /** UTC, zoneless. Always set: an announcement that names no start begins now. */
  StartsAt: string;
  EndsAt: string | null;
  /**
   * SuperAdmin only: the partner it is for (-1 = every partner). null keeps it on an edit and means everyone on a new one. An owner's
   * announcement is always for their own database's partner and module, and the server ignores these.
   */
  PartnerId: number | null;
  /** As `PartnerId`, for the module. */
  ModuleId: number | null;
}

/** The announcements the signed-in user has not read yet, newest first. Never an error: a failed read is an empty list. */
export function getMyAnnouncements() {
  return servicePost<Announcement[]>("WebGetMyAnnouncements", {});
}

/** Records the read receipt. Idempotent. */
export function markAnnouncementRead(announcementId: number) {
  return servicePost<boolean>("WebMarkAnnouncementRead", { announcementId });
}

/* ── SuperAdmin ── */

export function getAnnouncements() {
  return servicePost<Announcement[]>("WebGetAnnouncements", {});
}

/** Resolves to the saved announcement's id. */
export function saveAnnouncement(announcement: AnnouncementInput) {
  return servicePost<number>("WebSaveAnnouncement", { announcement });
}

export function deleteAnnouncement(announcementId: number) {
  return servicePost<boolean>("WebDeleteAnnouncement", { announcementId });
}

export function getAnnouncementReaders(announcementId: number) {
  return servicePost<AnnouncementReaders>("WebGetAnnouncementReaders", { announcementId });
}

/** Attaches (or replaces) the PDF of an announcement. The file travels as base64; the server checks it is a real PDF. */
export function saveAnnouncementAttachment(announcementId: number, fileName: string, contentBase64: string) {
  return servicePost<boolean>("WebSaveAnnouncementAttachment", { announcementId, fileName, contentBase64 });
}

export function removeAnnouncementAttachment(announcementId: number) {
  return servicePost<boolean>("WebRemoveAnnouncementAttachment", { announcementId });
}

/** The PDF itself. Only for an announcement the user is shown (a SuperAdmin can read any). */
export function getAnnouncementAttachment(announcementId: number) {
  return servicePost<AnnouncementAttachment>("WebGetAnnouncementAttachment", { announcementId });
}
